import React, { useEffect, useState, useCallback } from "react";
import { Search, CheckCircle, XCircle, Clock, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import AddManualPayment from "./AddManualPayment";
import api from "../utils/api";
import Pagination from "../components/PaginationComp";

const PaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [showAddPayment, setShowAddPayment] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 5,
  });

  const [stats, setStats] = useState({
    totalRevenue: 0,
    completedCount: 0,
    pendingCount: 0,
    failedCount: 0,
    totalPayments: 0,
  });

  /* ================= FETCH ALL STATISTICS ================= */
const fetchAllStats = useCallback(async () => {
  try {
    const res = await api.get(`admin/payments/stats`);
    setStats({
      totalRevenue: res.data.data?.totalRevenue || 0,
      completedCount: res.data.data?.completedCount || 0,
      pendingCount: res.data.data?.pendingCount || 0,
      failedCount: res.data.data?.failedCount || 0,
      totalPayments: res.data.data?.totalPayments || 0,
    });
  } catch (err) {
    console.error("Failed to fetch payment stats:", err);
    // Fallback to calculate from current data
    const completed = payments.filter(p => p.payment_status === "completed").length;
    const pending = payments.filter(p => p.payment_status === "pending").length;
    const failed = payments.filter(p => p.payment_status === "failed").length;
    const revenue = payments
      .filter(p => p.payment_status === "completed")
      .reduce((sum, p) => sum + (p.total_amount || 0), 0);
    
    setStats({
      totalRevenue: revenue,
      completedCount: completed,
      pendingCount: pending,
      failedCount: failed,
      totalPayments: payments.length,
    });
  }
}, [payments]);

useEffect(() => {
  fetchAllStats();
}, [fetchAllStats]);

  /* ================= FETCH PAYMENTS ================= */
  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`admin/payments`, {
        params: {
          page: currentPage,
          limit: pagination.limit,
          status: filterStatus !== "all" ? filterStatus : undefined,
          search: search || undefined,
        }
      });

      setPayments(res.data.data || []);
      setPagination(res.data.pagination);
      setCurrentPage(res.data.pagination.currentPage);
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.message || "Failed to fetch payments";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterStatus, search, pagination.limit]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  /* ================= VERIFY PAYMENT ================= */
  const verifyPayment = async (id) => {
    const toastId = toast.info(
      <div>
        <p className="font-medium mb-2">Verify Payment</p>
        <p className="text-sm text-gray-600 mb-4">Are you sure you want to verify this payment?</p>
        <div className="flex justify-end gap-2">
          <button
            onClick={() => toast.dismiss(toastId)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              try {
                await api.patch(`payments/${id}/verify`, {});
                toast.dismiss(toastId);
                toast.success("Payment verified successfully!");
                fetchPayments();
              } catch (error) {
                toast.dismiss(toastId);
                const message = error.response?.data?.message || "Failed to verify payment";
                toast.error(message);
              }
            }}
            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
          >
            Verify
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: true,
      }
    );
  };

  /* ================= HELPERS ================= */
  const statusIcon = (status) => {
    if (status === "completed")
      return <CheckCircle className="text-green-500 h-5 w-5" />;
    if (status === "failed")
      return <XCircle className="text-red-500 h-5 w-5" />;
    return <Clock className="text-yellow-500 h-5 w-5" />;
  };

  const statusBadge = (status) => {
    const colors = {
      completed: "bg-green-100 text-green-800 border border-green-200",
      pending: "bg-yellow-100 text-yellow-800 border border-yellow-200",
      failed: "bg-red-100 text-red-800 border border-red-200",
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-gray-100 text-gray-800"}`}>
        {status.toUpperCase()}
      </span>
    );
  };


  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleStatusFilter = (status) => {
    setFilterStatus(status);
    setCurrentPage(1);
  };

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    setSearch("");
    setFilterStatus("all");
    setCurrentPage(1);
  };

  /* ================= RENDER ================= */
  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="text-center md:text-left">
          <h2 className="text-2xl font-bold text-gray-800">Payment Management</h2>
          <p className="text-gray-600">Verify & monitor payments</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          {/* ADD PAYMENT */}
          <button
            onClick={() => setShowAddPayment(true)}
            className="flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2.5 rounded-lg hover:bg-green-700 transition-colors"
          >
            + Add Payment
          </button>

          {/* REFRESH */}
          <button
            onClick={handleRefresh}
            className="flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw size={18} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Total Revenue</p>
          <p className="text-3xl font-bold text-gray-800">₹{stats.totalRevenue.toLocaleString()}</p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Completed</p>
          <p className="text-3xl font-bold text-emerald-600">
            {stats.completedCount}
          </p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Pending</p>
          <p className="text-3xl font-bold text-amber-600">
            {stats.pendingCount}
          </p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Total Payments</p>
          <p className="text-3xl font-bold text-blue-600">
            {stats.totalPayments}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search user, email, transaction..."
            className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
          />
        </div>

        {/* Status Filter */}
        <div className="flex gap-2">
          <button
            onClick={() => handleStatusFilter("all")}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium ${
              filterStatus === "all"
                ? "bg-gray-800 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            All ({pagination.totalRecords})
          </button>
          <button
            onClick={() => handleStatusFilter("completed")}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium ${
              filterStatus === "completed"
                ? "bg-emerald-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Completed
          </button>
          <button
            onClick={() => handleStatusFilter("pending")}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium ${
              filterStatus === "pending"
                ? "bg-amber-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => handleStatusFilter("failed")}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium ${
              filterStatus === "failed"
                ? "bg-red-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Failed
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Transaction Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                // Loading Skeletons
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-24"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-28 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-20"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 bg-gray-200 rounded w-20"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-8 bg-gray-200 rounded w-20"></div>
                    </td>
                  </tr>
                ))
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-500">
                      <Search className="h-12 w-12 mb-3 text-gray-400" />
                      <p className="text-lg font-medium">No payments found</p>
                      <p className="text-sm mt-1">
                        {search || filterStatus !== "all"
                          ? "Try adjusting your filters"
                          : "No payment records available"}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                payments.map((p) => (
                  <tr key={p._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-800">
                          {new Date(p.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {new Date(p.createdAt).toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                        {p.transaction_id && (
                          <p className="text-xs text-gray-500 font-mono mt-1">
                            TXN: {p.transaction_id}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-800">{p.user_id?.owner || "N/A"}</p>
                        <p className="text-sm text-gray-500">{p.user_id?.email || "N/A"}</p>
                        {p.user_id?.phone && (
                          <p className="text-xs text-gray-500 mt-1">{p.user_id.phone}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-xl font-bold text-gray-800">₹{p.total_amount}</p>
                        {p.discount_amount > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            Discount: ₹{p.discount_amount}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800 capitalize">
                          {p.payment_method}
                        </span>
                        {p.payment_method === "manual" && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                            Manual
                          </span>
                        )}
                      </div>
                      {p.reference_number && (
                        <p className="text-xs text-gray-500 mt-1">
                          Ref: {p.reference_number}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          {statusIcon(p.payment_status)}
                          {statusBadge(p.payment_status)}
                        </div>
                        {p.payment_status === "completed" && p.verified_by && (
                          <p className="text-xs text-gray-500">
                            Verified by: {p.verified_by?.owner || "Admin"}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {p.payment_status === "pending" ? (
                        <button
                          onClick={() => verifyPayment(p._id)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                        >
                          Verify
                        </button>
                      ) : p.payment_status === "completed" ? (
                        <div className="flex items-center gap-1 text-emerald-600">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">Verified</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-600">
                Showing {payments.length} of {pagination.totalRecords} payments
                {filterStatus !== "all" && (
                  <span className="ml-2">
                    • Filtered by: <span className="font-medium">{filterStatus}</span>
                  </span>
                )}
                {search && (
                  <span className="ml-2">
                    • Search: <span className="font-medium">"{search}"</span>
                  </span>
                )}
              </p>
              <Pagination
                totalPages={pagination.totalPages}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        )}
      </div>

      {/* Add Manual Payment Modal */}
      <AddManualPayment
        show={showAddPayment}
        onClose={() => setShowAddPayment(false)}
        onSuccess={() => {
          fetchPayments();
          setShowAddPayment(false);
        }}
      />
    </div>
  );
};

export default PaymentManagement;